/** @jsxImportSource ../../i18n */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Send, ShieldAlert, ShieldCheck, Zap } from 'lucide-react';
import { basketTickets, tradingApi, errorOf } from '../../services/trading';
import { formatNumber } from '../../theme/format';
import { useToast } from '../../contexts/ToastContext';
import { Banner, Button, Checkbox, Dialog, Money, SegmentedControl, Spinner } from '../ui';
import { EnvBadge } from './EnvBadge';

const ICONS = { ok: [ShieldCheck, 'text-gain-400'], warn: [AlertTriangle, 'text-warn-400'], block: [ShieldAlert, 'text-loss-400'] };

const OFFSETS = [
  { value: 'limit', label: 'Limite ± 0,3 %' },
  { value: 'market', label: 'Au marché' },
];

// BasketDialog turns suggested orders (rebalancing, monthly deposit) into a
// real basket: checked order by order by the guard rails, sells sent first,
// buys sent once the sells are done. Drawn in the Trading universe.
export const BasketDialog = ({ open, onClose, origin, reason, lines }) => {
  const toast = useToast();
  const [status, setStatus] = useState(null);
  const [kind, setKind] = useState('limit');
  const [preview, setPreview] = useState(null);
  const [acks, setAcks] = useState({});
  const [liveOk, setLiveOk] = useState(false);
  const [state, setState] = useState({ loading: false, sending: false, error: '' });
  const [sent, setSent] = useState(null);

  const tickets = useMemo(() => basketTickets(lines || [], { kind }), [lines, kind]);

  useEffect(() => {
    if (!open) return;
    setPreview(null);
    setAcks({});
    setLiveOk(false);
    setSent(null);
    setState({ loading: true, sending: false, error: '' });
    tradingApi.status()
      .then((st) => {
        setStatus(st);
        setState({ loading: false, sending: false, error: '' });
      })
      .catch((err) => setState({ loading: false, sending: false, error: errorOf(err, 'Trading indisponible.') }));
  }, [open]);

  const env = status?.env || 'sim';
  const connected = Boolean(status?.apps?.[env]?.connected);

  const check = async () => {
    setState({ loading: true, sending: false, error: '' });
    try {
      setPreview(await tradingApi.previewBasket(tickets));
      setAcks({});
      setState({ loading: false, sending: false, error: '' });
    } catch (err) {
      setState({ loading: false, sending: false, error: errorOf(err, 'Vérification impossible.') });
    }
  };

  const ackOf = (i) => acks[i] || [];
  const missing = preview ? preview.orders.some((o, i) => o.needs_ack.some((id) => !ackOf(i).includes(id))) : true;
  const blockedAny = preview ? preview.orders.some((o) => o.blocked) : true;
  const canSend = preview && !blockedAny && !missing && (env !== 'live' || liveOk);

  const send = async () => {
    setState({ loading: false, sending: true, error: '' });
    try {
      const orders = await tradingApi.placeBasket({
        origin, reason, confirm_live: env === 'live' && liveOk, orders: preview.orders.map((o, i) => ({ ticket: o.ticket, acks: ackOf(i) })),
      });
      const placed = orders.filter((o) => o.status !== 'scheduled').length;
      const waiting = orders.length - placed;
      setSent({ placed, waiting });
      toast.success(waiting ? `Panier envoyé : ${placed} ordre${placed > 1 ? 's' : ''} parti${placed > 1 ? 's' : ''}, ${waiting} achat${waiting > 1 ? 's' : ''} après les ventes` : `Panier envoyé : ${placed} ordre${placed > 1 ? 's' : ''}`);
      setState({ loading: false, sending: false, error: '' });
    } catch (err) {
      const data = err?.response?.data;
      if (data?.preview) setPreview(data.preview);
      setState({ loading: false, sending: false, error: errorOf(err, 'Envoi impossible.') });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Passer ces ordres"
      description="Chaque ordre passe par les garde-fous du PEA. Les ventes partent d’abord ; les achats partent quand elles sont exécutées."
      size="lg"
      panelClassName="universe-trading"
      footer={sent ? (
        <>
          <Button as={Link} to="/trading/journal" variant="secondary" onClick={onClose}>Voir le journal</Button>
          <Button onClick={onClose}>Fermer</Button>
        </>
      ) : (
        <>
          <Button variant="secondary" onClick={onClose}>Annuler</Button>
          {preview
            ? <Button variant={env === 'live' ? 'danger' : 'primary'} leadingIcon={Send} loading={state.sending} disabled={!canSend} onClick={send}>{env === 'live' ? 'Envoyer en réel' : 'Envoyer le panier'}</Button>
            : <Button leadingIcon={ShieldCheck} loading={state.loading} disabled={!connected || !tickets.length} onClick={check}>Vérifier le panier</Button>}
        </>
      )}
    >
      <div className="space-y-4" data-testid="basket-dialog">
        <div className="flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center gap-2 text-eyebrow font-semibold uppercase tracking-[0.18em] text-trade-300"><Zap size={14} aria-hidden="true" />Trading</span>
          {status && <EnvBadge env={env} />}
          {!preview && !sent && <SegmentedControl ariaLabel="Type des ordres" value={kind} onChange={setKind} options={OFFSETS} />}
        </div>
        {state.loading && !status && <Spinner label="Connexion au trading…" />}
        {status && !connected && (
          <Banner tone="warn" action={<Button as={Link} to="/trading/reglages" size="sm" onClick={onClose}>Réglages</Button>}>
            Session de trading non connectée : enregistrez et connectez votre application Saxo.
          </Banner>
        )}
        {sent ? (
          <Banner tone="success" title="Panier envoyé">
            {`${sent.placed} ordre${sent.placed > 1 ? 's' : ''} envoyé${sent.placed > 1 ? 's' : ''}`}
            {sent.waiting > 0 && `, ${sent.waiting} achat${sent.waiting > 1 ? 's' : ''} programmé${sent.waiting > 1 ? 's' : ''} après les ventes`}
            . Suivez l’exécution dans le journal.
          </Banner>
        ) : (
          <ul className="divide-y divide-line rounded-control border border-line" aria-label="Ordres du panier">
            {(preview?.orders || tickets.map((ticket) => ({ ticket }))).map((o, i) => (
              <li key={`${o.ticket.symbol}-${o.ticket.buy_sell}`} className="px-3 py-2.5">
                <div className="flex items-center justify-between gap-3 text-sm">
                  <span className="min-w-0 truncate">
                    <span className={o.ticket.buy_sell === 'sell' ? 'text-loss-300' : 'text-gain-300'}>{o.ticket.buy_sell === 'sell' ? 'Vente' : 'Achat'}</span>
                    <span className="text-fg">{` ${formatNumber(o.ticket.amount)} ${o.ticket.symbol}`}</span>
                    <span className="text-fg-subtle">{o.ticket.price ? ` · limite ${formatNumber(o.ticket.price, { decimals: 2 })}` : ' · au marché'}</span>
                  </span>
                  {o.estimate?.value !== undefined && <Money value={o.estimate.value} decimals={0} className="text-fg" />}
                </div>
                {o.checks && (
                  <ul className="mt-1.5 space-y-1">
                    {o.checks.filter((c) => c.level !== 'ok').map((c) => {
                      const [Icon, color] = ICONS[c.level];
                      return (
                        <li key={c.id} className="flex items-start gap-2 text-xs">
                          <Icon size={14} aria-hidden="true" className={`mt-0.5 flex-shrink-0 ${color}`} />
                          <span className={c.level === 'block' ? 'text-loss-200' : 'text-warn-200'}>
                            {c.message}
                            {c.level === 'warn' && c.ack && (
                              <label className="mt-1 flex items-center gap-2 text-fg-muted">
                                <Checkbox
                                  checked={ackOf(i).includes(c.id)}
                                  onChange={(e) => setAcks((a) => ({ ...a, [i]: e.target.checked ? [...ackOf(i), c.id] : ackOf(i).filter((x) => x !== c.id) }))}
                                />
                                J’ai vérifié, je confirme
                              </label>
                            )}
                          </span>
                        </li>
                      );
                    })}
                    {o.checks.every((c) => c.level === 'ok') && <li className="flex items-center gap-2 text-xs text-fg-muted"><ShieldCheck size={14} aria-hidden="true" className="text-gain-400" />Garde-fous passés</li>}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        )}
        {preview && !sent && (
          <dl className="grid grid-cols-2 gap-3 text-sm">
            <div className="rounded-control border border-line px-3 py-2"><dt className="text-fg-muted">Ventes</dt><dd className="text-fg"><Money value={preview.sell_value} decimals={0} /></dd></div>
            <div className="rounded-control border border-line px-3 py-2"><dt className="text-fg-muted">Achats</dt><dd className="text-fg"><Money value={preview.buy_value} decimals={0} /></dd></div>
          </dl>
        )}
        {preview && !sent && env === 'live' && (
          <label className="flex items-start gap-3 rounded-control border border-loss-500/30 bg-loss-900/40 p-3 text-sm text-loss-100">
            <Checkbox checked={liveOk} onChange={(e) => setLiveOk(e.target.checked)} className="mt-0.5" />
            Je confirme ces ordres sur mon compte réel.
          </label>
        )}
        {state.error && <Banner tone="loss" compact role="alert">{state.error}</Banner>}
      </div>
    </Dialog>
  );
};

export default BasketDialog;
